/**
 * ActivityDetailLine — R5 / Stream FD (Feed-Detailtiefe)
 *
 * Rendert die v1-Detailzeilen eines Feed-Eintrags. Entscheidung R5:
 * Die Zeile ist IMMER sichtbar (kein Aufklappen), aber kompakt als
 * "Label: Wert · Label: Wert" formatiert.
 *
 * v1-Vorlagen:
 *   app/views/activities/_heartbeat.haml:33-51  (Status, Location, Distance to Asserted, Cell Type)
 *   app/views/activities/_speedtest.haml:31-49  (Status, Upload, Download, Latency)
 *   app/views/activities/_online.haml:2-29      (online / offline / changed listen addresses)
 *   app/views/activities/_data.haml             (Payload-Groesse)
 *   app/views/activities/_payment.haml:27-36    (Reward-Breakdown-Chips)
 *
 * Statuscodes kommen vom Backend als Slug (validity_key / result_key /
 * status / breakdown[].type) und werden hier uebersetzt; der englische
 * BE-Rohstring dient nur als Fallback.
 */
import { useTranslation } from 'react-i18next'
import type {
  ActivityDetails,
  DataDetails,
  HeartbeatDetails,
  OnlineDetails,
  RewardBreakdownDetails,
  SpeedtestDetails,
} from './api/activityApi'
import {
  formatBytes,
  formatDistance,
  formatLatLon,
  formatLatencyMs,
  formatListenAddrs,
  formatMultiplier,
  formatSpeedMbit,
} from './activityDetailFormat'

// --- Typ-Guards (die BE-Union ist ueber `kind` diskriminiert) ---

function isHeartbeat(kind: string, d: ActivityDetails): d is HeartbeatDetails {
  return kind === 'heartbeat' && 'validity_key' in d
}

function isSpeedtest(kind: string, d: ActivityDetails): d is SpeedtestDetails {
  return kind === 'speedtest' && 'result_key' in d
}

function isOnline(kind: string, d: ActivityDetails): d is OnlineDetails {
  return kind === 'online' && 'status' in d
}

function isData(kind: string, d: ActivityDetails): d is DataDetails {
  return kind === 'data' && 'payload_size_bytes' in d
}

function isRewardBreakdown(d: ActivityDetails): d is RewardBreakdownDetails {
  return 'breakdown' in d && Array.isArray((d as RewardBreakdownDetails).breakdown)
}

type DetailPart = { key: string; label: string; value: string }

export type ActivityDetailLineProps = {
  kind: string
  details?: ActivityDetails | null
  /** Nutzer-Einstellung account.use_metric — steuert die Distanz-Einheit. */
  useMetric?: boolean
}

export function ActivityDetailLine({ kind, details, useMetric = true }: ActivityDetailLineProps) {
  const { t } = useTranslation('activity')

  if (!details) return null

  // --- Reward-Breakdown: eigene Chip-Darstellung (v1 _payment.haml:27-36) ---
  if (isRewardBreakdown(details)) {
    // BUGFIX heliumtracker-web#86: post-HIP-138 liefert das BE fuer
    // Beacon/Witness weiterhin count > 0 (das Array enthaelt einen 0.0-Eintrag),
    // aber amount 0. Ein Chip "1x Beacon · 0.0000" ist nie sinnvoll.
    const entries = details.breakdown.filter((e) => e && e.count > 0 && e.amount > 0)
    if (entries.length === 0) return null
    return (
      <div className="activity-detail-line" data-testid="activity-detail" data-kind={kind}>
        {entries.map((entry) => (
          <span key={entry.type} className="pill pill-muted activity-detail-chip">
            {t('detail.breakdownChip', {
              count: entry.count,
              name: t(`detail.rewardTypes.${entry.type}`, { defaultValue: entry.human_name || entry.type }),
              amount: entry.amount.toFixed(4),
            })}
          </span>
        ))}
      </div>
    )
  }

  const parts: DetailPart[] = []
  const push = (key: string, label: string, value: string | null | undefined) => {
    if (value === null || value === undefined || value === '') return
    parts.push({ key, label, value })
  }

  if (isHeartbeat(kind, details)) {
    push(
      'status',
      t('detail.status'),
      t(`detail.heartbeatValidity.${details.validity_key}`, {
        defaultValue: details.validity_label || details.validity_key,
      }),
    )
    push('location', t('detail.location'), formatLatLon(details.lat, details.lon))
    // v1 zeigt die Distanz nur, wenn sie > 0 ist — das BE liefert dann bereits null.
    push('distance', t('detail.distanceToAsserted'), formatDistance(details.distance_to_asserted_m, useMetric))
    push('cellType', t('detail.cellType'), details.cell_type)
    push('rewardMultiplier', t('detail.rewardMultiplier'), formatMultiplier(details.reward_multiplier))
    push(
      'locationTrust',
      t('detail.locationTrust'),
      formatMultiplier(details.location_trust_score_multiplier),
    )
  } else if (isSpeedtest(kind, details)) {
    push(
      'status',
      t('detail.status'),
      t(`detail.speedtestResult.${details.result_key}`, {
        defaultValue: details.result_label || details.result_key,
      }),
    )
    push('upload', t('detail.uploadSpeed'), formatSpeedMbit(details.upload_speed_bps))
    push('download', t('detail.downloadSpeed'), formatSpeedMbit(details.download_speed_bps))
    push('latency', t('detail.latency'), formatLatencyMs(details.latency_ms))
  } else if (isOnline(kind, details)) {
    push(
      'status',
      t('detail.status'),
      t(`detail.onlineStatus.${details.status}`, { defaultValue: details.status }),
    )
    push('listenAddr', t('detail.listenAddr'), formatListenAddrs(details.listen_addrs))
  } else if (isData(kind, details)) {
    push('payloadSize', t('detail.payloadSize'), formatBytes(details.payload_size_bytes))
    // num_dcs ist im Datenbestand konstant 0 (oracle_sync.rb:1136) — nur zeigen wenn > 0.
    if (details.num_dcs != null && details.num_dcs > 0) {
      push('numDcs', t('detail.dataCredits'), String(details.num_dcs))
    }
  }

  if (parts.length === 0) return null

  return (
    <div
      className="activity-detail-line muted"
      data-testid="activity-detail"
      data-kind={kind}
      style={{ fontSize: '11px', marginTop: '2px' }}
    >
      {parts.map((part, i) => (
        <span key={part.key} data-detail={part.key}>
          {i > 0 && <span aria-hidden="true"> · </span>}
          <span className="activity-detail-label">{part.label}: </span>
          <span className="activity-detail-value">{part.value}</span>
        </span>
      ))}
    </div>
  )
}
